// ============================================================================
// Rescoring Events
// ============================================================================
// A rescoring event records that new external or internal information has
// changed our understanding of an item's scores on one dimension (effort,
// impact, urgency, etc.). It captures provenance — what we knew, what
// changed, and why — so the revision can be audited and prioritization
// decisions can be revisited if warranted.

import { humanizeAttributeKey } from '../../lib/sessionCriteriaFromTrack'

export type TriggerType =
  | 'technological_breakthrough'  // New tech reduces effort or changes approach
  | 'competitive_move'            // Competitor action shifts urgency or value
  | 'regulatory_change'           // New compliance requirement changes risk or urgency
  | 'market_shift'                // Market conditions changed value or urgency
  | 'internal_discovery'          // Scoping, design, or technical discovery
  | 'customer_feedback'           // Direct feedback changed our understanding
  | 'strategic_pivot'             // Company direction changed alignment or priority
  | 'other'

/** The seven dimensions rescoring ships with, each with its own anchors. Defaults, not the list. */
export type BuiltInRescoringAttributeKey =
  | 'effort'
  | 'impact'
  | 'urgency'
  | 'risk'
  | 'confidence'
  | 'strategic_alignment'
  | 'cost_of_delay'

/**
 * Any dimension the workspace scores on — a built-in or one it defined in
 * Settings → Attributes. Roadmap 1C V-10: until migration 1005 the database
 * accepted only the seven built-ins (chk_rescoring_attribute_key, 534); it now
 * takes any non-blank key.
 */
export type RescoringAttributeKey = string

export interface RescoringEvent {
  id: string
  workspaceId: string
  itemId: string

  triggerType: TriggerType
  triggerDescription: string

  attributeKey: RescoringAttributeKey
  previousScore: number
  revisedScore: number
  rationale: string

  revisitationRequired: boolean
  revisitationNotes?: string
  revisitationDue?: string  // ISO date string

  createdBy?: string
  createdAt: string  // ISO timestamp
}

// ─── Display metadata ─────────────────────────────────────────────────────────

export interface TriggerTypeDef {
  label: string
  description: string
  iconName: string  // Lucide icon name
}

export const TRIGGER_TYPE_DEFS: Record<TriggerType, TriggerTypeDef> = {
  technological_breakthrough: {
    label: 'Technological Breakthrough',
    description: 'New technology changes what is possible or how hard something is',
    iconName: 'Cpu',
  },
  competitive_move: {
    label: 'Competitive Move',
    description: 'A competitor action shifts the urgency or value of this item',
    iconName: 'Flag',
  },
  regulatory_change: {
    label: 'Regulatory Change',
    description: 'New compliance or legal requirements change risk or urgency',
    iconName: 'Shield',
  },
  market_shift: {
    label: 'Market Shift',
    description: 'Market conditions changed the opportunity size or timing',
    iconName: 'TrendingUp',
  },
  internal_discovery: {
    label: 'Internal Discovery',
    description: 'Scoping, design, or technical discovery changed the estimate',
    iconName: 'Search',
  },
  customer_feedback: {
    label: 'Customer Feedback',
    description: 'Direct feedback changed our understanding of value or effort',
    iconName: 'MessageSquare',
  },
  strategic_pivot: {
    label: 'Strategic Pivot',
    description: 'A change in company direction affects alignment or priority',
    iconName: 'Compass',
  },
  other: {
    label: 'Other',
    description: 'Another type of new information',
    iconName: 'MoreHorizontal',
  },
}

export interface AttributeDef {
  label: string
  min: number
  max: number
  /** Which direction is the "good" direction — used for delta coloring. */
  lowerIsBetter: boolean
  scaleLabels: Record<number, string>
}

export const RESCORING_ATTRIBUTE_DEFS: Record<BuiltInRescoringAttributeKey, AttributeDef> = {
  effort: {
    label: 'Effort',
    min: 1,
    max: 5,
    lowerIsBetter: true,
    scaleLabels: {
      1: 'Trivial',
      2: 'Small',
      3: 'Medium',
      4: 'Large',
      5: 'Extra Large',
    },
  },
  impact: {
    label: 'Impact',
    min: 1,
    max: 5,
    lowerIsBetter: false,
    scaleLabels: {
      1: 'Minimal',
      2: 'Limited',
      3: 'Moderate',
      4: 'Significant',
      5: 'Transformative',
    },
  },
  urgency: {
    label: 'Urgency',
    min: 1,
    max: 5,
    lowerIsBetter: false,
    scaleLabels: {
      1: 'No deadline',
      2: 'Flexible',
      3: 'Moderate',
      4: 'Pressing',
      5: 'Immediate',
    },
  },
  risk: {
    label: 'Risk',
    min: 1,
    max: 5,
    lowerIsBetter: true,
    scaleLabels: {
      1: 'Negligible',
      2: 'Low',
      3: 'Medium',
      4: 'High',
      5: 'Critical',
    },
  },
  confidence: {
    label: 'Confidence',
    min: 1,
    max: 4,
    lowerIsBetter: false,
    scaleLabels: {
      1: 'Speculative',
      2: 'Low',
      3: 'Reasonable',
      4: 'Committed',
    },
  },
  strategic_alignment: {
    label: 'Strategic Alignment',
    min: 0,
    max: 3,
    lowerIsBetter: false,
    scaleLabels: {
      0: 'None',
      1: 'Low',
      2: 'Medium',
      3: 'High',
    },
  },
  cost_of_delay: {
    label: 'Cost of Delay',
    min: 0,
    max: 4,
    lowerIsBetter: true,
    scaleLabels: {
      0: 'None',
      1: 'Low',
      2: 'Moderate',
      3: 'High',
      4: 'Severe',
    },
  },
}

/**
 * What rescoring shows for a dimension. A built-in keeps its scale and anchors
 * and takes the workspace's own name for it when there is one (Settings →
 * Attributes, migration 949); a dimension the workspace defined is shown by its
 * label on a plain 1–5 scale. Never throws on a key it does not know.
 */
export function rescoringAttributeDef(key: RescoringAttributeKey, workspaceLabel?: string | null): AttributeDef {
  const builtIn = (RESCORING_ATTRIBUTE_DEFS as Record<string, AttributeDef | undefined>)[key]
  const label = workspaceLabel?.trim() || builtIn?.label || humanizeAttributeKey(key)
  if (builtIn) return { ...builtIn, label }
  return { label, min: 1, max: 5, lowerIsBetter: false, scaleLabels: { 1: '1', 2: '2', 3: '3', 4: '4', 5: '5' } }
}
